import axios from 'axios';
import cookie from 'js-cookie';

// Base URL for ROI API endpoints
const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * ROI Service for managing Region of Interest data
 */
class ROIService {
    /**
     * Save ROIs for a specific floorplan
     * @param {string} floorplanId - The ID of the floorplan
     * @param {Array} rois - Array of ROI objects with name and polygon properties
     * @returns {Promise<Object>} Response containing roiIds
     */
    async saveROIs(floorplanId, rois) {
        try {
            // Transform frontend ROI format to backend DTO format
            const roiDTOs = rois.map((roi) => ({
                name: roi.name,
                polygon: roi.polygon.map((point) => [
                    Math.round(point[0] * 10000) / 10000, // Round to 4 decimal places
                    Math.round(point[1] * 10000) / 10000,
                ]),
            }));

            const response = await axios.post(
                `${API_BASE_URL}/floorplanRoi/createRoi/${floorplanId}`,
                roiDTOs,
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                    },
                }
            );

            return response.data;
        } catch (error) {
            throw this.handleAPIError(error);
        }
    }

    /**
     * Get ROIs for a specific floorplan
     * @param {string} floorplanId - The ID of the floorplan
     * @returns {Promise<Array>} Array of ROI objects
     */
    async getROIs(floorplanId) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/floorplanRoi/getRois/${floorplanId}`,
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                    },
                }
            );

            // Backend returns array directly, transform to frontend format
            const rois = (response.data || []).map((roiDTO) => ({
                id: roiDTO.roiId,
                name: roiDTO.name,
                polygon: roiDTO.polygon,
                dateCreated: roiDTO.dateCreated,
                dateUpdated: roiDTO.dateUpdated,
            }));

            return rois;
        } catch (error) {
            // If ROIs don't exist yet, return empty array
            if (error.response?.status === 404) {
                return [];
            }

            throw this.handleAPIError(error);
        }
    }

    /**
     * Update a specific ROI
     * @param {string} floorplanId - The ID of the floorplan
     * @param {string} roiId - The ID of the ROI to update
     * @param {Object} updates - Object containing fields to update (e.g., { name: 'New Name' })
     * @returns {Promise<Object>} Updated ROI data
     */
    async updateROI(floorplanId, roiId, updates) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/floorplanRoi/updateRoi/${floorplanId}/${roiId}`,
                updates,
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                    },
                }
            );

            return response.data;
        } catch (error) {
            throw this.handleAPIError(error);
        }
    }

    /**
     * Delete a specific ROI
     * @param {string} floorplanId - The ID of the floorplan
     * @param {string} roiId - The ID of the ROI to delete
     * @returns {Promise<Object>} Success response
     */
    async deleteROI(floorplanId, roiId) {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/floorplanRoi/deleteRoi/${floorplanId}/${roiId}`,
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                        'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                    },
                }
            );

            return response.data;
        } catch (error) {
            throw this.handleAPIError(error);
        }
    }

    /**
     * Validate ROI data before saving
     * @param {Array} rois - Array of ROI objects to validate
     * @returns {Object} Validation result with isValid boolean and errors array
     */
    validateROIs(rois) {
        const errors = [];

        if (!Array.isArray(rois)) {
            errors.push('ROIs must be provided as an array');
            return { isValid: false, errors };
        }

        rois.forEach((roi, index) => {
            if (!roi.name || typeof roi.name !== 'string') {
                errors.push(`ROI ${index + 1}: Name is required and must be a string`);
            }

            if (!roi.polygon || !Array.isArray(roi.polygon)) {
                errors.push(`ROI ${index + 1}: Polygon is required and must be an array`);
            } else if (roi.polygon.length < 3) {
                errors.push(`ROI ${index + 1}: Polygon must have at least 3 points`);
            } else {
                roi.polygon.forEach((point, pointIndex) => {
                    if (!Array.isArray(point) || point.length !== 2) {
                        errors.push(
                            `ROI ${index + 1}, Point ${pointIndex + 1}: Must be an array of 2 coordinates`
                        );
                    } else {
                        const [x, y] = point;
                        if (typeof x !== 'number' || typeof y !== 'number') {
                            errors.push(
                                `ROI ${index + 1}, Point ${pointIndex + 1}: Coordinates must be numbers`
                            );
                        } else if (x < 0 || x > 1 || y < 0 || y > 1) {
                            errors.push(
                                `ROI ${index + 1}, Point ${pointIndex + 1}: Coordinates must be between 0 and 1`
                            );
                        }
                    }
                });
            }
        });

        return {
            isValid: errors.length === 0,
            errors,
        };
    }

    /**
     * Handle API errors and provide user-friendly messages
     * @param {Error} error - The error object from axios
     * @returns {Error} Formatted error with user-friendly message
     */
    handleAPIError(error) {
        if (error.response) {
            // Server responded with error status
            const status = error.response.status;
            const message =
                error.response.data?.message ||
                error.response.data?.error ||
                error.response.data ||
                'Unknown server error';

            switch (status) {
                case 400:
                    return new Error(`Invalid request: ${message}`);
                case 401:
                    return new Error('Authentication required. Please log in again.');
                case 403:
                    return new Error('You do not have permission to perform this action.');
                case 404:
                    return new Error('Floorplan not found.');
                case 409:
                    return new Error('ROI data conflict. Please refresh and try again.');
                case 500:
                    return new Error('Server error. Please try again later.');
                default:
                    return new Error(`Server error (${status}): ${message}`);
            }
        } else if (error.request) {
            // Network error
            return new Error('Network error. Please check your connection and try again.');
        } else {
            // Other error
            return new Error(error.message || 'An unexpected error occurred.');
        }
    }
}

// Create a singleton instance
const roiService = new ROIService();

export default roiService;
